<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
  v-card.neutral200
    //- Top bar
    v-card-title.px-4
      y-button(outlined prepend-icon="mdi-arrow-left" color="neutral400" icon-color="neutral700" icon-size="20" size="xs" unset-min-width button-class="px-1" @click="checkCloseDialog")
      div.text-xl.font-semibold.ml-4 Treatment plan
      v-spacer
      yz-card.d-flex.align-center.mr-4(v-if="hasReservationsOrPreorders && !fromReservationPreorder" style="gap: 12px; background-color: #FFFAE6; border: 0.5px solid var(--v-neutral400); padding: 4px 4px 4px 8px;" radius="10px")
        span.yz-medium-body-sm.text-neutral-700 Reservation & pre-order: {{ reservationPreorderCount }}
        yz-btn(label="View" size="sm" background-color="white" outlined, @click="onClickViewReservations")
      y-button(label="Save & close" size="sm" :loading="isLoading" @click="saveData")

    v-card-text.neutral300.pa-0
      template(v-if="isSetting")
        v-skeleton-loader.pa-4(type="list-item-three-line@6")
        //- v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
      v-container.pa-6(v-else style="max-width: 1440px;")
        v-row.mb-5(v-if="showPatientInfo && consultation && Object.keys(consultation).length > 0" no-gutters)
          v-col(cols="7" style="padding-right: 6px;")
            patient-info-section.fill-height
          v-col(cols="5" style="padding-left: 6px;")
            treatment-plan-vital-signs.fill-height(v-if="!isHq")
              
        .header
          div.text-sm.neutral700--text Search and select the item you want add into treatment panel
          v-row.mt-2(dense)
            v-col(cols="5")
              search-treatment-dropdown(size="sm" input-text-size="text-sm" :isOTC="isOTC" :inventory-only="inventoryOnly" :allow-out-of-stock="allowOutOfStock" @added="handleAddTreatmentProduct($event)")
            v-col.shrink
              y-button(label="Add item in bulk" unset-min-width size="sm" button-class="px-3" @click="showAddBulkDialog = true")
            v-col.shrink
              y-button(label="Add item from past visit" unset-min-width size="sm" button-class="px-3" @click="showAddPastDialog = true")
        
        .content
          v-form(ref="form")
            div.add-box.mt-5(v-if="treatments.filter(f => !f.delete).length === 0")
              div.d-flex.align-center.justify-center.text-center.fill-height
                div
                  v-icon(size="32" color="neutral500") $yzi-file-02
                  div.text-sm.font-medium.neutral700--text.mt-2 No treatment and services selected yet
                  div.text-sm.neutral500--text.mt-1 Selected items ans services will show up here
            div.pt-2(v-else)
              template(v-for="(treatment, idx) in treatments")
                treatment-plan-item(:treatment.sync="treatment" :key="`trp-${idx}`" :allow-out-of-stock="allowOutOfStock" :from-reservation-preorder="fromReservationPreorder" @duplicate="handleDuplicateItem")
    v-divider
    v-card-actions.pa-0
      v-container.px-6.py-5(style="max-width: 1440px; justify-items: end;")
        div.text-md
          span.font-medium.neutral500--text Total
          span.font-semibold.neutral700--text.ml-4 {{ totalPrice | displayPrice($currency) }}

    .dialogs
      add-bulk-treatment-dialog(v-if="showAddBulkDialog" :open.sync="showAddBulkDialog" :isOTC="isOTC" :inventory-only="inventoryOnly" :allow-out-of-stock="allowOutOfStock" @insert="handleAddBulkTreatment")
      add-past-treatment-dialog(v-if="showAddPastDialog" :open.sync="showAddPastDialog" :isOTC="isOTC" :inventory-only="inventoryOnly" :allow-out-of-stock="allowOutOfStock" @insert="handleAddPastTreatment")
      view-reservations-preorders-dialog(v-if="showReservationPreorderDialog" :open.sync="showReservationPreorderDialog" :reservations="reservations" :preorders="preorders" @confirm="onConfirmReservationSelection")
      alert-confirmation-dialog(v-if="showAlert" :open.sync="showAlert" @save="saveData" @discard="closeDialog")
        template(#title)
          div.text-md.font-semibold.neutral700--text Changes detected
        template(#content)
          div.text-md.neutral700--text Unsaved changes detected. Do you want to save or discard the changes?
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PatientInfoSection from '@/components/clinic/consultation/PatientInfoSection.vue'
import TreatmentPlanVitalSigns from '@/components/clinic/consultation/TreatmentPlanVitalSigns.vue'
import SearchTreatmentDropdown from '@/components/clinic/consultation/SearchTreatmentDropdown.vue'
import TreatmentPlanItem from '@/components/clinic/consultation/TreatmentPlanItem.vue'
import AddBulkTreatmentDialog from '@/components/clinic/consultation/AddBulkTreatmentDialog.vue'
import AddPastTreatmentDialog from '@/components/clinic/consultation/AddPastTreatmentDialog.vue'
import AlertConfirmationDialog from '@/components/clinic/consultation/AlertConfirmationDialog.vue'
import ViewReservationsPreordersDialog from '@/components/clinic/registration/ViewReservationsPreordersDialog.vue'

export default {
  components: { PatientInfoSection, TreatmentPlanVitalSigns, SearchTreatmentDropdown, TreatmentPlanItem, AddBulkTreatmentDialog, AddPastTreatmentDialog, AlertConfirmationDialog, ViewReservationsPreordersDialog },
  props: {
    open: {
      type: Boolean,
    },
    editMode: {
      type: Boolean,
      default: false
    },
    bulkMode: {
      type: Boolean,
      default: false
    },
    pastMode: {
      type: Boolean,
      default: false
    },
    showPatientInfo: {
      type: Boolean,
      default: true
    },
    isOTC: {
      type: Boolean,
      default: false
    },
    inventoryOnly: {
      type: Boolean,
      default: false
    },
    allowOutOfStock: {
      type: Boolean,
      default: false
    },
    fromReservationPreorder: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isSetting: false,
      isLoading: false,
      showAddBulkDialog: false,
      showAddPastDialog: false,
      showAlert: false,
      showReservationPreorderDialog: false,
      reservations: [],
      preorders: [],
      treatments: [],
      oriTreatments: [],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        valid_blood_pressure: [ v => /^\d+\/\d+$/.test(v) || 'Blood pressure must be valid', ]
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      patient: 'treatment/getPatient',
      consultation: 'treatment/getConsultation',
      existingTreatments: 'treatment/getTreatments',
      activeMedications: 'treatment/getActiveMedications',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    hasChanges () {
      if (this.treatments.filter(f => !f.delete).length && !this.editMode) {
        return true
      }
      if (this.editMode && JSON.stringify(this.treatments) !== JSON.stringify(this.oriTreatments)) {
        return true
      }
      return false
    },
    hasReservationsOrPreorders () {
      return this.reservations.length > 0 || this.preorders.length > 0
    },
    reservationPreorderCount () {
      return this.reservations.length + this.preorders.length
    },
    totalPrice () {
      return (this.treatments.filter(f => !f.delete).reduce((acc, cur) => acc + (cur.rate * cur.quantity), 0) * this.$currency.multiplier)
    },
  },
  watch: {
  },
  mounted () {
    this.isSetting = true
    // Set a delay to display the loading state while waiting for the data to be processed
    setTimeout(() => {
      this.setData()
      setTimeout(() => {
        this.oriTreatments = JSON.parse(JSON.stringify(this.treatments))
      }, 1500)
    }, 50)
    if (this.patient?.id && !this.fromReservationPreorder) {
      this.fetchReservationsAndPreorders()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchReservationsAndPreorders () {
      try {
        const [reservationsResp, preordersResp] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params: { patient: this.patient.id, status: 'active', page_size: -1 } }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params: { patient: this.patient.id, status: 'active', page_size: -1 } })
        ])
        if (reservationsResp.data?.results) {
          this.reservations = reservationsResp.data.results.filter(r => r.products.length > 0)
        }
        if (preordersResp.data?.results) {
          this.preorders = preordersResp.data.results.filter(r => r.products.length > 0)
        }
      } catch {
        // silently ignore
      }
    },
    onClickViewReservations () {
      this.showReservationPreorderDialog = true
    },
    async onConfirmReservationSelection (items) {
      if (!items.length) {
        return
      }
      this.isLoading = true
      const failed = []
      const succeeded = []
      for (const item of items) {
        try {
          const endpoint = item.type === 'reservation'
            ? `/clinics/${this.company.slug}/reservations/${item.id}/fulfill/`
            : `/clinics/${this.company.slug}/preorders/${item.id}/fulfill/`
          await this.$axios.post(endpoint, { registration_pid: this.leadPid })
          succeeded.push(item)
        } catch (err) {
          console.error(err)
          failed.push(item)
        }
      }
      const succeededReservationIds = succeeded.filter(i => i.type === 'reservation').map(i => i.id)
      const succeededPreorderIds = succeeded.filter(i => i.type === 'preorder').map(i => i.id)
      this.reservations = this.reservations.filter(r => !succeededReservationIds.includes(r.id))
      this.preorders = this.preorders.filter(p => !succeededPreorderIds.includes(p.id))
      if (succeeded.length) {
        await this.refetchTreatments()
      }
      if (failed.length) {
        const failedList = failed.map(i => `${i.type} #${i.id}`).join(', ')
        this.showMessage(['error', `Failed to fulfill: ${failedList}`])
      } else if (succeeded.length) {
        this.showMessage(['success', 'Reservation & pre-order fulfilled'])
      }
      this.isLoading = false
    },
    async refetchTreatments () {
      try {
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, { params: { fields: 'treatments' } })
        this.$store.commit('treatment/setTreatments', resp.data.treatments)
        this.treatments = []
        this.existingTreatments.forEach(t => this.handleAddExistItem(t, true))
        this.oriTreatments = JSON.parse(JSON.stringify(this.treatments))
      } catch (err) {
        console.error(err)
      }
    },
    setData () {
      if (this.bulkMode) {
        this.showAddBulkDialog = true
      } else if (this.pastMode) {
        this.showAddPastDialog = true
      } else if (this.editMode && this.existingTreatments.length) {
        let tempTreatments = this.existingTreatments
        for (let i = 0; i < tempTreatments.length; i++ ) {
          this.handleAddExistItem(tempTreatments[i], true)
        }
      }
      this.isSetting = false
    },
    handleAddExistItem (treatment, isEdit = false) {
      // arrange object similar to treatment_products api
      let data = {
        item_id: treatment.id || null,
        id: treatment.product.id,
        branch_for: treatment.product.branch_for,
        name: treatment.product.name,
        inventory_enabled: treatment.product.inventory_enabled,
        inventory_qty: treatment.product.inventory_qty,
        sku: treatment.product.sku,
        supplier: treatment.product.supplier,
        available_qty: treatment.product.available_qty,
        stock: treatment.stock,
        type: treatment.product.type,
        price: treatment.product.price,
        medication: treatment.product.medication,
        document_template: treatment.product.document_template,
        // field use as input
        quantity: treatment.quantity,
        rate: (Number(treatment.price / this.$currency.multiplier)),
        priceTier: {
          id: treatment?.extra_data?.price_tier || null,
          name: treatment?.extra_data?.price_tier_name || null,
          price: treatment.product.price || null,
        },
        dosageUnit: treatment?.extra_data?.dosage_unit || null,
        dosageValue: treatment?.extra_data?.dosage_value || null,
        frequency: treatment?.extra_data?.frequency || null,
        indication: treatment?.extra_data?.indication || null,
        instruction: treatment?.extra_data?.instruction || null,
        duration: treatment?.extra_data?.duration || null,
        precaution: treatment?.extra_data?.precaution || null,
        activeMedication: this.checkActiveMedication(treatment.product),
        document: null,
        uom: treatment.product.uom,
        inventory_uom: treatment.product.inventory_uom,
        uom_conversion_enabled: treatment.product.uom_conversion_enabled,
        uom_conversions: treatment.product.uom_conversions,
        selectedUom: treatment.product.uom_conversion_enabled
          ? (treatment.uom || treatment.product.uom || (treatment.product.uom_conversions?.length ? treatment.product.uom_conversions[treatment.product.uom_conversions.length - 1].convert_to : null))
          : (treatment.uom || treatment.product.uom || null),
        // custom field
        edit: isEdit,
        delete: false,
      }
      if (treatment.product.type === 'documents') {
        data.document = { ...treatment.extra_data }
      }
      // package treatment
      if (treatment.product.type === 'bundle') {
        let items = []
        let tempBundles = treatment.bundles
        for (let j = 0; j < tempBundles.length; j++ ) {
          let item = {
            item_id: tempBundles[j].id || null,
            id: tempBundles[j].product.id,
            name: tempBundles[j].product.name,
            quantity_per_set: tempBundles[j].quantity_per_set,
            inventory_enabled: tempBundles[j].product.inventory_enabled,
            inventory_qty: tempBundles[j].product.inventory_qty,
            sku: tempBundles[j].product.sku,
            supplier: tempBundles[j].product.supplier,
            available_qty: tempBundles[j].product.available_qty,
            stock: tempBundles[j].stock,
            medication: tempBundles[j].product.medication,
            // field use as input
            dosageUnit: tempBundles[j]?.extra_data?.dosage_unit || null,
            dosageValue: tempBundles[j]?.extra_data?.dosage_value || null,
            frequency: tempBundles[j]?.extra_data?.frequency || null,
            indication: tempBundles[j]?.extra_data?.indication || null,
            instruction: tempBundles[j]?.extra_data?.instruction || null,
            duration: tempBundles[j]?.extra_data?.duration || null,
            precaution: tempBundles[j]?.extra_data?.precaution || null,
            activeMedication: this.checkActiveMedication(tempBundles[j].product),
            uom: tempBundles[j].product.uom,
            inventory_uom: tempBundles[j].product.inventory_uom,
            uom_conversion_enabled: tempBundles[j].product.uom_conversion_enabled,
            uom_conversions: tempBundles[j].product.uom_conversions,
            selectedUom: tempBundles[j].product.uom_conversion_enabled
              ? (tempBundles[j].uom || tempBundles[j].product.uom || (tempBundles[j].product.uom_conversions?.length ? tempBundles[j].product.uom_conversions[tempBundles[j].product.uom_conversions.length - 1].convert_to : null))
              : (tempBundles[j].uom || tempBundles[j].product.uom || null),
            // custom field
            edit: isEdit,
            delete: false,
          }
          items.push(item)
        }
        data.bundle = {
          available_qty: null,
          treatments: items
        }
      }
      this.treatments.push(data)
    },
    handleAddTreatmentProduct (item) {
      // from treatment_products api
      let treatment = {
        ...item,
        // field use as input
        quantity: item?.medication?.default_qty || 1,
        rate: (Number(item.price) / this.$currency.multiplier),
        priceTier: null,
        dosageUnit: item?.medication?.dosage_unit || null,
        dosageValue: item?.medication?.dosage_value || null,
        frequency: item?.medication?.frequency || null,
        indication: item?.medication?.indication || null,
        instruction: item?.medication?.instruction || null,
        duration: item?.medication?.duration || null,
        precaution: item?.medication?.precaution || null,
        activeMedication: this.checkActiveMedication(item),
        document: null,
        selectedUom: item.uom_conversion_enabled
          ? (item.uom || (item.uom_conversions?.length ? item.uom_conversions[item.uom_conversions.length - 1].convert_to : null))
          : (item.uom || null),
        // custom field
        edit: false,
        delete: false,
      }
      // package treatment
      if (item.type === 'bundle') {
        let bundles = []
        item.bundle.treatments.forEach(o => {
          let bundle = {
            ...o,
            // field use as input
            dosageUnit: o?.medication?.dosage_unit || null,
            dosageValue: o?.medication?.dosage_value || null,
            frequency: o?.medication?.frequency || null,
            indication: o?.medication?.indication || null,
            instruction: o?.medication?.instruction || null,
            duration: o?.medication?.duration || null,
            precaution: o?.medication?.precaution || null,
            activeMedication: this.checkActiveMedication(o),
            selectedUom: o.uom_conversion_enabled
              ? (o.uom || (o.uom_conversions?.length ? o.uom_conversions[o.uom_conversions.length - 1].convert_to : null))
              : (o.uom || null),
            // custom field
            delete: false,
          }
          bundles.push(bundle)
        })
        treatment.bundle = {
          available_qty: null,
          treatments: bundles
        }
      }
      this.treatments.push(treatment)
    },
    handleDuplicateItem (item) {
      let treatment = {
        ...item,
        edit: false,
        delete: false,
      }
      this.treatments.push(treatment)
    },
    handleAddBulkTreatment (items) {
      if (!items.length) {
        return
      }
      items.forEach(o => {
        this.handleAddTreatmentProduct(o)
      })
      this.showAddBulkDialog = false
    },
    handleAddPastTreatment (items) {
      if (!items.length) {
        return
      }
      items.forEach(o => {
        this.handleAddExistItem(o)
      })
      this.showAddPastDialog = false
    },
    checkActiveMedication (treatment) {
      if (treatment.medication && treatment.medication.id) {
        if (this.activeMedications.find(f => f.m_id === treatment.medication.id)) {
          return true
        }
      }
      return false
    },
    checkErrorPriceTier () {
      let treatments = JSON.parse(JSON.stringify(this.treatments)).filter(f => !f.delete)
      let temp = treatments.find(f => !f.priceTier && !((f.type === 'documents' && !f.document_template.price_tier_enabled) || (f.type === 'bundle')))
      if (temp) {
        return true
      }
      return false
    },
    checkErrorDosageUnit () {
      let treatments = JSON.parse(JSON.stringify(this.treatments)).filter(f => !f.delete)
      let temp = treatments.find(f => ((f.dosageUnit || f.dosageValue) && (!f.dosageUnit || !f.dosageValue)))
      let temp2 = treatments.filter(fl => fl.type === 'bundle').map(m => m.bundle.treatments).flat().find(f => ((f.dosageUnit || f.dosageValue) && (!f.dosageUnit || !f.dosageValue)))
      if (temp || temp2) {
        return true
      }
      return false
    },
    checkMedicine (treatment) {
      if (treatment.medication && treatment.medication.id) {
        return true
      }
      return false
    },
    checkDocument (treatment) {
      if (treatment.type === 'documents') {
        return true
      }
      return false
    },
    checkPackage (treatment) {
      if (treatment.type === 'bundle') {
        return true
      }
      return false
    },
    saveData () {
      this.isLoading = true
      if (!this.$refs.form.validate()) {
        this.isLoading = false
        return this.showMessage(['error', 'Please fill in all required fields and fix any errors'])
      } if (this.checkErrorPriceTier()) {
        this.isLoading = false
        return this.showMessage(['error', 'Please select a valid price tier for all applicable items'])
      } if (this.checkErrorDosageUnit()) {
        this.isLoading = false
        return this.showMessage(['error', 'Please enter the correct dosage amount and unit'])
      }
      let products = []
      let treatments = this.treatments
      for (let i = 0; i < treatments.length; i++ ) {
        if (!treatments[i].edit && treatments[i].delete) {
          continue
        }
        let data = {
          product: treatments[i].id,
          quantity: treatments[i].quantity,
          price: Math.round(Number(treatments[i].rate) * this.$currency.multiplier)
        }
        if (treatments[i].item_id) {
          data.id = treatments[i].item_id
        }
        if (treatments[i].delete) {
          data.quantity = 0
          if (this.allowOutOfStock) {
            data.skip_inventory_update = true
          }
          products.push(data)
          continue
        }
        if (this.allowOutOfStock) {
          data.skip_inventory_update = true
        }
        if (treatments[i].uom_conversion_enabled && treatments[i].selectedUom) {
          data.uom = treatments[i].selectedUom
        }
        if (treatments[i].priceTier) {
          data.extra_data = {
            price_tier: treatments[i].priceTier.id,
            price_tier_name: treatments[i].priceTier.name,
          }
        }
        if (this.checkMedicine(treatments[i])) {
          data.extra_data = {
            ...data.extra_data,
            dosage_unit: treatments[i].dosageUnit,
            dosage_value: treatments[i].dosageValue,
            frequency: treatments[i].frequency,
            duration: treatments[i].duration,
            instruction: treatments[i].instruction,
            precaution: treatments[i].precaution,
            indication: treatments[i].indication,
          }
        }
        if (this.checkDocument(treatments[i])) {
          data.extra_data = {
            ...data.extra_data,
            ...treatments[i].document
          }
        }
        if (this.checkPackage(treatments[i])) {
          let bundles = []
          let bundle = treatments[i].bundle.treatments
          for (let j = 0; j < bundle.length; j++ ) {
            if (!bundle[j].edit && bundle[j].delete) {
              continue
            }
            let item = {
              product: bundle[j].id,
              quantity_per_set: bundle[j].quantity_per_set,
            }
            if (bundle[j].item_id) {
              item.id = bundle[j].item_id
            }
            if (bundle[j].delete) {
              item.quantity_per_set = 0
              bundles.push(item)
              continue
            }
            if (bundle[j].medication) {
              item.extra_data = {
                dosage_unit: bundle[j].dosageUnit,
                dosage_value: bundle[j].dosageValue,
                duration: bundle[j].duration,
                frequency: bundle[j].frequency,
                indication: bundle[j].indication,
                instruction: bundle[j].instruction,
                precaution: bundle[j].precaution,
              }
            }
            bundles.push(item)
          }
          data.bundles = bundles
        }
        products.push(data)
      }
      // bulk treatment api
      let payload = { products: products }
      const endpoint = `/clinics/${this.company.slug}/invoices/${this.leadPid}`
      this.$axios.patch(endpoint, payload)
        .then(resp => {
          this.updateActivityLog(endpoint, payload, resp.data, 'PATCH', resp.data.id, 'consultation', `Update treatment plan (${this.leadPid})`)
          this.updateActiveMedication()
        })
        .catch((err) => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.status === 400 && err.response?.data?.code === '444101') {
            message = 'Item quantity more than available stock. Please adjust the quantity'
          }       
          if (err.response?.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
          this.isLoading = false
        })
    },
    async updateActiveMedication () {
      let medicines = [
        ...this.treatments.filter(f => f.medication && f.medication.id),
        ...this.treatments.filter(f => f.type === 'bundle').map(m => m.bundle.treatments).flat().filter(f => f.medication && f.medication.id)
      ]
      if (!medicines.length) {
        this.finalizeProcess()
        return
      } 
      let uniqueMedicines = Array.from(new Map(medicines.map(item => [item.id, item])).values())
      let activeMeds = uniqueMedicines.filter(f => f.activeMedication)
      let inactiveMeds = uniqueMedicines.filter(f => !f.activeMedication)
      // activate medication
      for (let i = 0; i < activeMeds.length; i++ ) {
        if (!this.checkActiveMedication(activeMeds[i])) {
          const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
          const endpoint = `clinics/${companySlug}/active_medications`
          let payload = {
            medication: activeMeds[i].medication.id,
            patient: this.patient.id
          }
          await this.$axios.post(endpoint, payload)
            .then(resp => {
              this.updateActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id, 'active_medication', 'add active medication')
            })
            .catch(() => {})
        }
      }
      // deactivate medication
      for (let j = 0; j < inactiveMeds.length; j++ ) {
        if (this.checkActiveMedication(inactiveMeds[j])) {
          let actmed = this.activeMedications.find(f => f.m_id === inactiveMeds[j].medication.id)
          const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
          const endpoint = `clinics/${companySlug}/active_medications/${actmed.id}`
          await this.$axios.delete(endpoint)
            .catch(() => {})
            .finally(() => {
              this.updateActivityLog(endpoint, null, null, 'DELETE', inactiveMeds[j].medication.id, 'active_medication', 'deactivate medication')
            })
        }
      }
      this.finalizeProcess()
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    finalizeProcess () {
      this.showMessage(['success', 'Treatment plan successfully updated'])
      this.$emit('updated')
      this.showAlert = false
      this.isLoading = false
    },
    checkCloseDialog () {
      if (this.hasChanges) {
        this.showAlert = true
      } else {
        this.closeDialog()
      }
    },
    closeDialog () {
      this.$emit('closed')
      this.showAlert = false
      this.showDialog = false
    },
  },
}
</script>

<style lang="scss" scoped>
.add-box {
  border-radius: 10px;
  border: var(--v-neutral400) dashed 2px;
  height: 400px;
}
</style>